import { useState, useCallback } from "react";
import { RequirePermission } from "@/components/RequirePermission";
import { use1099Summary, Vendor1099Row } from "@/hooks/use1099Summary";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

const fmt = (n: number) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const maskTin = (ein: string | null) => {
  if (!ein) return "—";
  if (ein.length >= 4) return "***-**-" + ein.slice(-4);
  return "****";
};

const thresholdBadge = (amt: number) => {
  if (amt < 600) return <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">Under $600</Badge>;
  if (amt <= 1000) return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">$600–$1,000</Badge>;
  return <Badge variant="destructive">Over $1,000</Badge>;
};

const Vendor1099Report = () => {
  const [year, setYear] = useState(currentYear);
  const { data: rows = [], isLoading } = use1099Summary(year);

  const exportCsv = useCallback(() => {
    if (!rows.length) return;
    const headers = ["Vendor Name", "TIN", "Address", "City", "State", "ZIP", "Box 1 - Nonemployee Compensation"];
    const csv = [
      headers.join(","),
      ...rows
        .filter((r) => r.exceeds_1099_threshold)
        .map((r) =>
          [
            r.vendor_name,
            r.ein ?? "",
            r.billing_address ?? "",
            r.billing_city ?? "",
            r.billing_state ?? "",
            r.billing_zip ?? "",
            r.ytd_paid.toFixed(2),
          ]
            .map((v) => `"${v}"`)
            .join(",")
        ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `1099-NEC-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, year]);

  const overThreshold = rows.filter((r) => r.exceeds_1099_threshold);
  const missingW9 = rows.filter((r) => r.w9_required_missing);

  return (
    <RequirePermission anyOf={["view_financials"]}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">1099-NEC Report</h1>
          <div className="flex items-center gap-3">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!overThreshold.length}>
              <Download className="h-4 w-4 mr-1" /> Export 1099 CSV
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Total Vendors Paid</p>
            <p className="text-2xl font-bold">{rows.length}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">Over $600 Threshold</p>
            <p className="text-2xl font-bold text-yellow-600">{overThreshold.length}</p>
          </div>
          <div className="rounded-lg border p-4 border-destructive/30">
            <p className="text-sm text-muted-foreground">Missing W-9</p>
            <p className="text-2xl font-bold text-destructive">{missingW9.length}</p>
          </div>
        </div>

        {/* Warning banner */}
        {missingW9.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive font-medium">
              {missingW9.length} vendor{missingW9.length > 1 ? "s" : ""} over $600 threshold with no W-9 on file.
              IRS requires W-9 before issuing 1099-NEC.
            </p>
          </div>
        )}

        {isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : !rows.length ? (
          <p className="text-muted-foreground">No paid vendor bills found for {year}.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>TIN (Masked)</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead className="text-right">YTD Paid</TableHead>
                <TableHead>Threshold</TableHead>
                <TableHead>W-9</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.vendor_id}>
                  <TableCell className="font-medium">{r.vendor_name}</TableCell>
                  <TableCell className="font-mono text-sm">{maskTin(r.ein)}</TableCell>
                  <TableCell className="text-sm capitalize">{r.tax_classification ?? "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(r.ytd_paid)}</TableCell>
                  <TableCell>{thresholdBadge(r.ytd_paid)}</TableCell>
                  <TableCell>
                    {r.w9_on_file ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </RequirePermission>
  );
};

export default Vendor1099Report;
